// src/features/people/components/WorksSection.jsx - CON RESTRICCIONES PARA PERSONAS DADAS DE BAJA
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Row, Col } from 'react-bootstrap';
import { FaPlus, FaBuilding, FaMapMarkerAlt } from 'react-icons/fa';
import dayjs from 'dayjs';

// Componentes
import TableWrapper from '../../../components/TableWrapper';
import FilterSelect from '../../../components/FilterSelect';
import AssignWorkModal from './AssignWorkModal';

// Hooks
import { useColumnFilter } from '../../../hooks/useColumnFilter';

// Constantes
import { FORMATO_FECHA } from '../../../utils/constants';

// ✅ AGREGADA PROP isPersonDeactivated
const WorksSection = ({ works = [], personId, onDataChange, showSnackbar, isPersonDeactivated = false }) => {
  const navigate = useNavigate();
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [filteredWorks, setFilteredWorks] = useState(works);

  const clientValueGetter = useCallback((work) => {
    if (work.customers && work.customers.length > 0) {
      return work.customers[0].businessName || '';
    }
    return '';
  }, []);

  const { 
    filterOptions: locationOptions, 
    activeFilters: activeLocations,
    handleOptionsUpdate: handleLocationOptionsUpdate,
    handleFiltersChange: handleLocationChange,
    filterData: filterByLocation 
  } = useColumnFilter(works, 'location', null, `person-${personId}-works`);

  const { 
    filterOptions: clientOptions, 
    activeFilters: activeClients,
    handleOptionsUpdate: handleClientOptionsUpdate,
    handleFiltersChange: handleClientChange,
    filterData: filterByClient 
  } = useColumnFilter(works, 'customers', clientValueGetter, `person-${personId}-works`);

  useEffect(() => {
    let result = [...works];
    
    if (activeLocations && activeLocations.length > 0) {
      result = filterByLocation(result);
    }
    
    if (activeClients && activeClients.length > 0) {
      result = filterByClient(result);
    }
    
    setFilteredWorks(result);
  }, [works, activeLocations, activeClients, filterByLocation, filterByClient]);

  const handleViewWork = (work) => {
    sessionStorage.setItem('returnToRoute', `/people/${personId}`);
    navigate(`/works/${work.id}`);
  };

  // ✅ VALIDACIÓN AGREGADA
  const handleOpenAssignModal = () => {
    if (isPersonDeactivated) {
      showSnackbar('warning', 'No se pueden asignar obras a una persona dada de baja');
      return;
    }
    setShowAssignModal(true);
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
  };

  const handleWorkAssigned = () => {
    if (showSnackbar) {
      showSnackbar('success', 'Obra asignada correctamente');
    }
    
    if (onDataChange) {
      onDataChange();
    }
  };

  const workColumns = useMemo(() => [
    { 
      headerName: 'Nombre', 
      field: 'name', 
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      headerName: 'Ubicación', 
      field: 'location', 
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      headerName: 'Fecha Inicio', 
      field: 'startDate', 
      width: 130,
      valueFormatter: params => params.value ? dayjs(params.value).format(FORMATO_FECHA) : '',
      filter: 'agTextColumnFilter',
      filterValueGetter: params => {
        return params.data.startDate ? dayjs(params.data.startDate).format(FORMATO_FECHA) : '';
      },
      floatingFilter: true
    },
    { 
      headerName: 'Cliente', 
      field: 'customers',
      flex: 1,
      valueGetter: params => {
        const customers = params.data.customers;
        return customers && customers.length > 0 ? customers[0].businessName : '';
      },
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      headerName: 'Observaciones', 
      field: 'observations', 
      flex: 1.5,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    }
  ], []);

  return (
    <div className="d-flex flex-column h-100">
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex gap-2">
              <FilterSelect
                options={locationOptions}
                activeFilters={activeLocations}
                onFiltersChange={handleLocationChange}
                onOptionsUpdate={handleLocationOptionsUpdate}
                title="Filtrar por ubicación"
                filterKey={`person-${personId}-works-locations`}
                icon={<FaMapMarkerAlt />}
                variant="outline-secondary"
              />
              
              <FilterSelect
                options={clientOptions}
                activeFilters={activeClients}
                onFiltersChange={handleClientChange}
                onOptionsUpdate={handleClientOptionsUpdate}
                title="Filtrar por cliente"
                filterKey={`person-${personId}-works-clients`}
                icon={<FaBuilding />}
                variant="outline-secondary"
              />
            </div>
            
            {/* ✅ BOTÓN DESHABILITADO SI ESTÁ DADA DE BAJA */}
            <Button 
              size="sm"
              className="d-flex align-items-center"
              onClick={handleOpenAssignModal}
              disabled={isPersonDeactivated}
            >
              <FaPlus className="me-1" /> Asignar Obra
            </Button>
          </div>
        </Col>
      </Row>
      
      <div className="flex-grow-1" style={{ height: 'calc(100vh - 280px)' }}>
        <TableWrapper 
          columnDefs={workColumns}
          rowData={filteredWorks}
          tableType="obras"
          onView={handleViewWork}
          customGridOptions={{
            pagination: true,
            paginationPageSize: 20,
            domLayout: 'autoHeight'
          }}
        />
      </div>
      
      <AssignWorkModal
        show={showAssignModal}
        onHide={handleCloseAssignModal}
        personId={personId}
        currentWorks={works}
        onWorkAssigned={handleWorkAssigned}
      />
    </div>
  );
};

export default WorksSection;